import { useState } from "react"

import type { UseMutationResult } from "@tanstack/react-query"

export type PdfExportStart = `popup_blocked` | `started`

/** Long enough for the new tab to have fetched the document from the URL. */
const BLOB_RELEASE_DELAY_MS = 60_000

/** Static holding page for the new tab, so it is not blank while the server renders. */
const placeholderHtml = (subject: string): string =>
  `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>Generating ${subject} PDF</title></head><body style="margin:0;display:grid;place-items:center;min-height:100vh;font:16px system-ui,sans-serif;background:#14161a;color:#e8eaed"><p>Generating the ${subject} PDF. This tab will open it when it is ready.</p></body></html>`

export type PdfExportTab<Variables> = {
  readonly start: (variables: Variables) => PdfExportStart
  readonly error: unknown
  readonly isError: boolean
  readonly isLoading: boolean
  readonly popupBlocked: boolean
  readonly tabClosed: boolean
}

/**
 * Opens the PDF's tab in the same gesture as the click or key that asked for
 * it — a tab opened after the render would be a blocked popup — then points it
 * at the generated document. `subject` names it in the holding page.
 */
export function usePdfExportTab<Variables>(
  exportPdf: UseMutationResult<Blob, unknown, Variables>,
  subject: string,
): PdfExportTab<Variables> {
  const [popupBlocked, setPopupBlocked] = useState(false)
  const [tabClosed, setTabClosed] = useState(false)

  const start = (variables: Variables): PdfExportStart => {
    const tab = window.open(``, `_blank`)
    if (!tab) {
      setPopupBlocked(true)
      return `popup_blocked`
    }
    setPopupBlocked(false)
    setTabClosed(false)
    // Rendering takes tens of seconds. Say so in the tab, or the seller waits
    // on a blank page, decides it is broken, and closes the document.
    tab.document.write(placeholderHtml(subject))
    tab.document.close()

    void exportPdf
      .mutateAsync(variables)
      .then((pdf) => {
        // Navigating a window the seller already closed is a silent no-op, which
        // would leave the rail claiming a success that went nowhere.
        if (tab.closed) {
          setTabClosed(true)
          return
        }
        const documentUrl = URL.createObjectURL(pdf)
        tab.location.href = documentUrl
        // The tab has the bytes once it has loaded; holding the URL past that
        // pins the whole PDF in memory for the rest of the seller's session.
        setTimeout(() => URL.revokeObjectURL(documentUrl), BLOB_RELEASE_DELAY_MS)
      })
      .catch(() => {
        // The rail explains the refusal; a blank tab would read as a success.
        tab.close()
      })

    return `started`
  }

  return {
    start,
    error: exportPdf.error,
    isError: exportPdf.isError,
    isLoading: exportPdf.isLoading,
    popupBlocked,
    tabClosed,
  }
}
